import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {Action, Card, closeCurrentDialog, openDialog} from 'platform/components';

import {isNil, isNotNil} from 'ramda';

import {GetVehicleApiResponseV2} from '@dms/api/shared';
import {useGetLatestAiVehicleServiceIntervalsQuery} from '@dms/api/vehicle';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';

import {
  composePath,
  Nullish,
  RequiredTestIdProps,
  suffixTestId,
  useNavigate,
  buildArray,
} from 'shared/web';

import {VehicleServiceIntervalsAIAttributes} from './components/VehicleServiceIntervalsAIAttributes';
import {VehicleServiceIntervalsAttributes} from './components/VehicleServiceIntervalsAttributes';
import {VehicleServiceIntervalsGenerateForm} from './components/VehicleServiceIntervalsGenerateForm';

interface VehicleServiceIntervalsCardProps extends RequiredTestIdProps {
  vehicleData: GetVehicleApiResponseV2 | Nullish;
}

export function VehicleServiceIntervalsCard(props: VehicleServiceIntervalsCardProps) {
  const navigate = useNavigate();

  const vehicleId = props.vehicleData?.id;
  const isAiEnabled = isFeatureEnabled(featureFlags.AI_VEHICLE_SERVICE_INTERVALS);

  // The latest persisted generation (plan + suggestions + AI summary). 404 (no generation yet)
  // surfaces as a query error and simply leaves the AI columns empty.
  const latestQuery = useGetLatestAiVehicleServiceIntervalsQuery(
    isAiEnabled && isNotNil(vehicleId) ? {vehicleId} : skipToken
  );

  const navigateToEdit = () => {
    if (isNil(vehicleId)) {
      return;
    }
    return navigate(composePath(vehiclesRoutes.editServiceIntervals, {params: {id: vehicleId}}));
  };

  const generateServiceIntervals = () => {
    const vehicleData = props.vehicleData;
    if (isNil(vehicleData?.id)) {
      return;
    }

    openDialog(
      <VehicleServiceIntervalsGenerateForm
        vehicleData={vehicleData}
        onSuccess={closeCurrentDialog}
        onCancel={closeCurrentDialog}
        data-testid={suffixTestId('generateServiceIntervalsForm', props)}
      />,
      {title: i18n.t('entity.vehicle.actions.generateServiceIntervals')}
    );
  };

  return (
    <Card
      title={i18n.t('entity.vehicle.labels.serviceIntervals')}
      actions={buildArray<Action>()
        .when(isAiEnabled, {
          type: 'button',
          variant: 'link',
          size: 'small',
          leftIcon: 'action/magic_wand',
          onClick: generateServiceIntervals,
          'data-testid': suffixTestId('generate', props),
          title: i18n.t('entity.vehicle.actions.generateServiceIntervals'),
        })
        .add({
          type: 'button',
          variant: 'link',
          size: 'small',
          leftIcon: 'image/edit',
          onClick: navigateToEdit,
          'data-testid': suffixTestId('edit', props),
          title: i18n.t('general.actions.edit'),
        })}
    >
      {isAiEnabled ? (
        <VehicleServiceIntervalsAIAttributes
          vehicleData={props.vehicleData}
          record={latestQuery.data}
          data-testid={suffixTestId('serviceIntervals', props)}
        />
      ) : (
        <VehicleServiceIntervalsAttributes
          vehicleData={props.vehicleData}
          data-testid={suffixTestId('serviceIntervals', props)}
        />
      )}
    </Card>
  );
}
